"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { useMemo } from "react";

import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import { cn } from "~/lib/utils";

function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        "ekv:flex ekv:flex-col ekv:gap-6",
        "ekv:has-[>[data-slot=checkbox-group]]:gap-3 ekv:has-[>[data-slot=radio-group]]:gap-3",
        className,
      )}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = "legend",
  ...props
}: React.ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        "ekv:mb-3 ekv:font-medium",
        "ekv:data-[variant=legend]:text-base",
        "ekv:data-[variant=label]:text-sm",
        className,
      )}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        "ekv:group/field-group ekv:@container/field-group ekv:flex ekv:w-full ekv:flex-col ekv:gap-7 ekv:data-[slot=checkbox-group]:gap-3 ekv:*:data-[slot=field-group]:gap-4",
        className,
      )}
      {...props}
    />
  );
}

const fieldVariants = cva(
  "ekv:group/field ekv:flex ekv:w-full ekv:gap-3 ekv:data-[invalid=true]:text-destructive",
  {
    variants: {
      orientation: {
        vertical: ["ekv:flex-col ekv:*:w-full ekv:[&>.sr-only]:w-auto"],
        horizontal: [
          "ekv:flex-row ekv:items-center",
          "ekv:*:data-[slot=field-label]:flex-auto",
          "ekv:has-[>[data-slot=field-content]]:items-start ekv:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
        ],
        responsive: [
          "ekv:flex-col ekv:*:w-full ekv:[&>.sr-only]:w-auto ekv:@md/field-group:flex-row ekv:@md/field-group:items-center ekv:@md/field-group:*:w-auto",
          "ekv:@md/field-group:*:data-[slot=field-label]:flex-auto",
          "ekv:@md/field-group:has-[>[data-slot=field-content]]:items-start ekv:@md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
        ],
      },
    },
    defaultVariants: {
      orientation: "vertical",
    },
  },
);

function Field({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
}

function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn(
        "ekv:group/field-content ekv:flex ekv:flex-1 ekv:flex-col ekv:gap-1.5 ekv:leading-snug",
        className,
      )}
      {...props}
    />
  );
}

function FieldLabel({
  className,
  ...props
}: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="field-label"
      className={cn(
        "ekv:group/field-label ekv:peer/field-label ekv:flex ekv:w-fit ekv:gap-2 ekv:leading-snug ekv:group-data-[disabled=true]/field:opacity-50",
        "ekv:has-[>[data-slot=field]]:w-full ekv:has-[>[data-slot=field]]:flex-col ekv:has-[>[data-slot=field]]:rounded-md ekv:has-[>[data-slot=field]]:border ekv:*:data-[slot=field]:p-4",
        "ekv:has-data-[state=checked]:bg-primary/5 ekv:has-data-[state=checked]:border-primary ekv:dark:has-data-[state=checked]:bg-primary/10",
        className,
      )}
      {...props}
    />
  );
}

function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        "ekv:flex ekv:w-fit ekv:items-center ekv:gap-2 ekv:text-sm ekv:leading-snug ekv:font-medium ekv:group-data-[disabled=true]/field:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        "ekv:text-muted-foreground ekv:text-sm ekv:leading-normal ekv:font-normal ekv:group-has-data-[orientation=horizontal]/field:text-balance",
        "ekv:last:mt-0 ekv:nth-last-2:-mt-1 ekv:[[data-variant=legend]+&]:-mt-1.5",
        "ekv:[&>a:hover]:text-primary ekv:[&>a]:underline ekv:[&>a]:underline-offset-4",
        className,
      )}
      {...props}
    />
  );
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn(
        "ekv:relative ekv:-my-2 ekv:h-5 ekv:text-sm ekv:group-data-[variant=outline]/field-group:-mb-2",
        className,
      )}
      {...props}
    >
      <Separator className="ekv:absolute ekv:inset-0 ekv:top-1/2" />
      {children && (
        <span
          className="ekv:bg-background ekv:text-muted-foreground ekv:relative ekv:mx-auto ekv:block ekv:w-fit ekv:px-2"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  );
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<"div"> & {
  errors?: Array<{ message?: string } | undefined>;
}) {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors?.length) {
      return null;
    }

    const uniqueErrors = [
      ...new Map(errors.map((error) => [error?.message, error])).values(),
    ];

    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message;
    }

    return (
      <ul className="ekv:ml-4 ekv:flex ekv:list-disc ekv:flex-col ekv:gap-1">
        {uniqueErrors.map(
          (error, index) =>
            error?.message && <li key={index}>{error.message}</li>,
        )}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn(
        "ekv:text-destructive ekv:text-sm ekv:font-normal",
        className,
      )}
      {...props}
    >
      {content}
    </div>
  );
}

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
};
